import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Camera, X, RotateCcw, Zap, ZapOff } from 'lucide-react'
// @ts-ignore - jsQR não tem tipagem oficial
import jsQR from 'jsqr'

import Button from './ui/Button'
import Modal from './Modal'

interface QRScannerProps {
  isOpen: boolean
  onClose: () => void
  onScan: (data: string) => void
  title?: string
}

interface ScanResult {
  data: string
  timestamp: Date
}

const QRScanner: React.FC<QRScannerProps> = ({
  isOpen,
  onClose,
  onScan,
  title = "Scanner QR Code"
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isScanning, setIsScanning] = useState(false)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [scanResults, setScanResults] = useState<ScanResult[]>([])
  const [flashOn, setFlashOn] = useState(false)
  const intervalRef = useRef<any>()
  const animationRef = useRef<number>()
  const lastScanTime = useRef<number>(0)
  const frameSkipCount = useRef<number>(0)
  const processedHashes = useRef<Set<string>>(new Set())

  // Inicializar câmera quando o modal abrir
  useEffect(() => {
    if (isOpen) {
      initializeCamera()
    } else {
      stopCamera()
    }

    return () => {
      stopCamera()
    }
  }, [isOpen])

  // Buscar dispositivos disponíveis
  useEffect(() => {
    if (isOpen) {
      getDevices()
    }
  }, [isOpen])

  const getDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const videoDevices = devices.filter(device => device.kind === 'videoinput')
      setDevices(videoDevices)
      
      // Selecionar câmera traseira por padrão
      const backCamera = videoDevices.find(device => 
        device.label.toLowerCase().includes('back') || 
        device.label.toLowerCase().includes('traseira')
      )
      setSelectedDeviceId(backCamera?.deviceId || videoDevices[0]?.deviceId || '')
    } catch (err) {
      console.error('Erro ao obter dispositivos:', err)
      setError('Erro ao acessar dispositivos de câmera')
    }
  }

  const initializeCamera = async () => {
    try {
      setError('')
      
      // Parar stream anterior se existir
      if (stream) {
        stream.getTracks().forEach(track => track.stop())
      }

      const constraints: MediaStreamConstraints = {
        video: {
          deviceId: selectedDeviceId || undefined,
          facingMode: selectedDeviceId ? undefined : { ideal: 'environment' },
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 480 },
          frameRate: { ideal: 30, max: 30 }
        }
      }

      const newStream = await navigator.mediaDevices.getUserMedia(constraints)
      setStream(newStream)

      if (videoRef.current) {
        videoRef.current.srcObject = newStream
        videoRef.current.play()
        
        videoRef.current.onloadedmetadata = () => {
          startScanning()
        }
      }
    } catch (err: any) {
      console.error('Erro ao inicializar câmera:', err)
      if (err.name === 'NotAllowedError') {
        setError('Permissão para câmera negada. Por favor, permita o acesso à câmera.')
      } else if (err.name === 'NotFoundError') {
        setError('Nenhuma câmera encontrada no dispositivo.')
      } else {
        setError('Erro ao acessar a câmera: ' + err.message)
      }
    }
  }

  const stopCamera = () => {
    setIsScanning(false)
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
    }

    if (stream) {
      stream.getTracks().forEach(track => track.stop())
      setStream(null)
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
  }

  const startScanning = () => {
    if (!videoRef.current || !canvasRef.current) return

    setIsScanning(true)
    lastScanTime.current = Date.now()
    frameSkipCount.current = 0
    
    // Usar requestAnimationFrame para melhor performance
    const scanLoop = () => {
      if (isScanning) {
        scanQRCode()
        animationRef.current = requestAnimationFrame(scanLoop)
      }
    }
    
    scanLoop()
  }

  const scanQRCode = () => {
    if (!videoRef.current || !canvasRef.current || !isScanning) return

    const now = Date.now()
    
    // Throttle: processar no máximo a cada 200ms para melhor performance
    if (now - lastScanTime.current < 200) {
      return
    }

    // Skip frames para reduzir carga de processamento
    frameSkipCount.current++
    if (frameSkipCount.current % 3 !== 0) {
      return
    }

    const video = videoRef.current
    const canvas = canvasRef.current
    const context = canvas.getContext('2d')

    if (!context || video.readyState !== video.HAVE_ENOUGH_DATA) return

    // Usar resolução menor para melhor performance
    const targetWidth = 640
    const targetHeight = 480
    
    canvas.width = targetWidth
    canvas.height = targetHeight
    
    // Desenhar com suavização desligada para melhor performance
    context.imageSmoothingEnabled = false
    context.drawImage(video, 0, 0, targetWidth, targetHeight)

    // Obter dados da imagem apenas da área central (ROI - Region of Interest)
    const roiSize = 300
    const roiX = (targetWidth - roiSize) / 2
    const roiY = (targetHeight - roiSize) / 2
    
    const imageData = context.getImageData(roiX, roiY, roiSize, roiSize)
    
    // Usar jsQR com configurações otimizadas
    const code = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'dontInvert'
    })

    lastScanTime.current = now

    if (code) {
      handleScanResult(code.data)
    }
  }


  // Função para gerar hash simples de string
  const simpleHash = useCallback((str: string): string => {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Converter para 32bit integer
    }
    return hash.toString()
  }, [])

  const handleScanResult = useCallback((data: string) => {
    // Gerar hash do resultado para cache
    const dataHash = simpleHash(data)
    
    // Verificar se já foi processado recentemente
    if (processedHashes.current.has(dataHash)) {
      return
    }

    // Adicionar ao cache e limpar entradas antigas
    processedHashes.current.add(dataHash)
    if (processedHashes.current.size > 10) {
      const entries = Array.from(processedHashes.current)
      processedHashes.current.clear()
      entries.slice(-5).forEach(hash => processedHashes.current.add(hash))
    }

    const result: ScanResult = {
      data,
      timestamp: new Date()
    }

    setScanResults(prev => [result, ...prev.slice(0, 4)]) // Manter últimos 5 resultados
    onScan(data)
    
    // Feedback visual/sonoro
    if (navigator.vibrate) {
      navigator.vibrate(200)
    }

    // Pausar escaneamento por 1 segundo após sucesso para evitar spam
    setIsScanning(false)
    setTimeout(() => {
      if (videoRef.current) {
        setIsScanning(true)
        startScanning()
      }
    }, 1000)

    // Limpar cache após 3 segundos
    setTimeout(() => {
      processedHashes.current.delete(dataHash)
    }, 3000)
  }, [simpleHash, onScan])

  const toggleFlash = async () => {
    if (!stream) return

    try {
      const track = stream.getVideoTracks()[0]
      const capabilities = track.getCapabilities()
      
      if ((capabilities as any).torch) {
        await track.applyConstraints({
          advanced: [{ torch: !flashOn } as any]
        })
        setFlashOn(!flashOn)
      }
    } catch (err) {
      console.error('Erro ao controlar flash:', err)
    }
  }

  const switchCamera = async () => {
    if (devices.length <= 1) return

    const currentIndex = devices.findIndex(device => device.deviceId === selectedDeviceId)
    const nextIndex = (currentIndex + 1) % devices.length
    const nextDevice = devices[nextIndex]
    
    setSelectedDeviceId(nextDevice.deviceId)
    await initializeCamera()
  }

  const formatScanResult = (data: string): string => {
    try {
      const parsed = JSON.parse(data)
      return `${parsed.type?.toUpperCase()} #${parsed.id}`
    } catch {
      return data.length > 30 ? `${data.substring(0, 30)}...` : data
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="lg"
    >
      <div className="space-y-4">
        {error ? (
          <div className="text-center py-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-red-600 mb-4">
                <Camera className="h-12 w-12 mx-auto mb-2" />
                <p className="font-medium">{error}</p>
              </div>
              <Button
                onClick={initializeCamera}
                leftIcon={<RotateCcw className="h-4 w-4" />}
              >
                Tentar Novamente
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Área do vídeo */}
            <div className="relative bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                className="w-full h-64 object-cover"
                autoPlay
                playsInline
                muted
              />
              
              {/* Overlay de escaneamento */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-48 h-48 border-2 border-white rounded-lg relative">
                  <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-blue-500"></div>
                  <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-blue-500"></div>
                  <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-blue-500"></div>
                  <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-blue-500"></div>
                  
                  {isScanning && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-full h-0.5 bg-red-500 animate-pulse"></div>
                    </div>
                  )}
                </div>
              </div>

              {/* Controles da câmera */}
              <div className="absolute top-4 right-4 flex gap-2">
                {devices.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={switchCamera}
                    className="bg-black/50 text-white hover:bg-black/70"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleFlash}
                  className="bg-black/50 text-white hover:bg-black/70"
                >
                  {flashOn ? <Zap className="h-4 w-4" /> : <ZapOff className="h-4 w-4" />}
                </Button>
              </div>

              {/* Status de escaneamento */}
              <div className="absolute bottom-4 left-4 right-4">
                <div className="bg-black/50 text-white text-center py-2 px-4 rounded">
                  {isScanning ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-sm">Escaneando...</span>
                    </div>
                  ) : (
                    <span className="text-sm">Posicione o QR code na área destacada</span>
                  )}
                </div>
              </div>
            </div>

            {/* Canvas oculto para processamento */}
            <canvas ref={canvasRef} className="hidden" />

            {/* Resultados recentes */}
            {scanResults.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Resultados Recentes</h4>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {scanResults.map((result, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-white p-2 rounded border text-sm"
                    >
                      <span className="font-mono">{formatScanResult(result.data)}</span>
                      <span className="text-gray-500 text-xs">
                        {result.timestamp.toLocaleTimeString('pt-PT')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Instruções */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Camera className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Como usar:</p>
                  <ul className="space-y-1 text-xs">
                    <li>• Posicione o QR code dentro da área destacada</li>
                    <li>• Mantenha o dispositivo estável</li>
                    <li>• Certifique-se de que há luz suficiente</li>
                    <li>• O escaneamento é automático</li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Botões de ação */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Fechar
          </Button>
          {!error && (
            <Button
              onClick={isScanning ? () => setIsScanning(false) : startScanning}
              leftIcon={isScanning ? <X className="h-4 w-4" /> : <Camera className="h-4 w-4" />}
              variant={isScanning ? "outline" : "primary"}
            >
              {isScanning ? 'Parar' : 'Iniciar'} Escaneamento
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default QRScanner